/**
 * Le categorie dei dispacci, ripulite: i sinonimi ridotti a una voce sola
 * e le voci geografiche separate da quelle tematiche.
 *
 * Censite a monte il 30/09/2026 su tutti i 1.592 dispacci che hanno una
 * categoria (il campo è la stringa vuota sul resto dell'archivio). Le
 * voci distinte sono una quarantina, scritte a mano in redazione, e due
 * difetti si vedevano nei menu:
 *
 * - **sinonimi**: «IT» (6 dispacci) e «Information Technology» (5) sono
 *   la stessa rubrica, «Societa» è «Società» senza l'accento. Nel menu
 *   erano due voci, e scegliendone una si perdeva l'altra metà;
 * - **aree fra i temi**: «Maghreb» (18), «Regione» (12), «Asia» (11),
 *   «Medio Oriente» (10), «Europa» (7), «Africa» (4) non dicono *di che
 *   cosa* parla un dispaccio ma *dove*. Stavano nel menu «Categoria»
 *   accanto a Economia ed Energia, e chi cercava un'area la trovava nel
 *   menu sbagliato. Adesso le pagine le offrono nel filtro geografico.
 *
 * Il parametro nell'indirizzo resta `?cat=` con la voce **canonica**; a
 * monte si chiede con `$in` su tutte le grafie note (`variantiCategoria`),
 * perché il filtro di Strapi è per valore esatto. Un `?cat=IT` scritto a
 * mano, o arrivato da un link vecchio, si legge come «Information
 * Technology».
 *
 * Modulo senza direttiva e senza `lib/nd-client`: lo leggono sia le rotte
 * sia i componenti client della ricerca.
 */

/** Le grafie note di ogni voce canonica, compresa la canonica. Solo i
 *  casi misurati a monte: una voce che non compare qui è canonica di sé. */
const GRAFIE: Record<string, string[]> = {
  "Information Technology": ["Information Technology", "IT"],
  Società: ["Società", "Societa"],
  "Fare impresa": ["Fare impresa", "Fare Impresa"],
};

/** Le voci che sono un luogo e non un tema. «Regione» è la rubrica dei
 *  dispacci a cavallo di più paesi (la NIS fra Romania e Bulgaria, il
 *  gasdotto trans-sahariano): geografica anche lei, senza un paese solo. */
const GEOGRAFICHE = new Set(["Maghreb", "Regione", "Asia", "Medio Oriente", "Europa", "Africa"]);

/** La chiave di confronto: senza maiuscole, senza accenti, spazi
 *  compressi. Due grafie con la stessa chiave sono la stessa voce. */
function chiave(s: string): string {
  return s
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase("it");
}

const CANONICA = new Map<string, string>();
for (const [canonica, grafie] of Object.entries(GRAFIE))
  for (const g of grafie) CANONICA.set(chiave(g), canonica);
for (const g of GEOGRAFICHE) CANONICA.set(chiave(g), g);

/** La voce canonica di una categoria. Una voce sconosciuta resta com'è,
 *  ripulita dagli spazi: è il caso normale, non un errore. */
export function canonicaCategoria(raw: string | null | undefined): string {
  const v = (raw ?? "").replace(/\s+/g, " ").trim();
  if (!v) return "";
  return CANONICA.get(chiave(v)) ?? v;
}

/** Le grafie da chiedere a monte per una voce canonica. */
export function variantiCategoria(cat: string): string[] {
  const c = canonicaCategoria(cat);
  if (!c) return [];
  return GRAFIE[c] ?? [c];
}

/** La categoria è un luogo? */
export function categoriaGeografica(cat: string): boolean {
  return GEOGRAFICHE.has(canonicaCategoria(cat));
}

export type VoceCategoria = { name: string; count: number };

/** Il censimento a monte, con i sinonimi fusi (i conteggi si sommano) e
 *  diviso nelle due faccette. Ordine: peso, poi nome. */
export function faccetteCategorie(voci: VoceCategoria[]): {
  temi: VoceCategoria[];
  aree: VoceCategoria[];
} {
  const m = new Map<string, number>();
  for (const v of voci) {
    const c = canonicaCategoria(v.name);
    if (c) m.set(c, (m.get(c) ?? 0) + v.count);
  }
  const tutte = [...m.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "it"));
  return {
    temi: tutte.filter((v) => !GEOGRAFICHE.has(v.name)),
    aree: tutte.filter((v) => GEOGRAFICHE.has(v.name)),
  };
}
